<template>
  <div>
    <btn
      :icon="icons.mdiHexagram"
      @click="modalState = true"
    >
      Best Practice
      <br />
      Guidelines
    </btn>

    <v-dialog
      v-model="modalState"
      width="800"
      persistent
      scrollable
    >
      <v-card class="bpg-modal-card">
        <v-card-title class="bpg-header pa-4">
          Best Practice Care Guidelines
          <v-spacer></v-spacer>
          <btn
            color="secondary"
            class="buttons"
            :icon="icons.mdiClose"
            @click="modalState = false"
          >
            Close
          </btn>
        </v-card-title>

        <!-- Main Outer Box Wrapper -->
        <div class="bpg-main-container mx-4 mb-4">
          <v-tabs
            v-model="activeTab"
            background-color="Primary"
            class="bpg-tabs"
            fixed-tabs
          >
            <v-tab
              v-for="(tab, tabIndex) in tabContent"
              :key="`tab_${tabIndex}`"
            >
              <!-- The name of navigation tab -->
              {{ tab.name }}
            </v-tab>
          </v-tabs>

          <v-card-text class="bpg-content pa-4">
            <v-tabs-items v-model="activeTab">
              <v-tab-item
                v-for="(tab, tabIndex) in tabContent"
                :key="`tab_item_${tabIndex}`"
                class="pl-4"
              >
                <!-- Similar to the name of the navigation tab, the title for this guideline -->
                <h1>{{ tab.title }}:</h1>
                <ol
                  class="pl-6 py-6"
                >
                  <!-- Refer to data property: tabContent for documentation-->
                  <li
                    v-for="(section, sectionIndex) in tab.section"
                    :key="`section_${sectionIndex}`"
                  >
                    {{ section.title }}
                    <ol type="a">
                      <li
                        v-for="(info, infoIndex) in section.info"
                        :key="`sectionInfo_${infoIndex}`"
                      >
                        <!-- If info is an object, display the string in HTML, display sub list if array has content -->
                        <div v-if="checkIfObj(info)">
                          <div v-html="info.main"></div>
                          <ol type="i">
                            <li
                              v-for="(subInfo, subInfoIndex) in info.sub"
                              :key="`info_${subInfoIndex}`"
                            >
                              {{ subInfo }}
                            </li>
                          </ol>
                        </div>

                        <div v-else>
                          {{ info }}
                        </div>
                      </li>
                    </ol>
                  </li>
                </ol>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mdiClose, mdiHexagram } from '@mdi/js'

export default {

  data() {
    return {
      modalState: false,
      icons: {
        mdiHexagram,
        mdiClose,
      },
      diabeticBPG: null,

      activeTab: 0,

      // @typedef {[Object]} tabContent
      // @property {String} name the name for the tab header
      // @property {String} title the string displayed in the v-tab-item element
      // @typedef {[Object]} section includes both the title of each bullet point section and the bullet points displayed to the user
      // @property {String} title title of the section of bullet points
      // @property {[String, Object]} info the individual bullet points separated into an array. if the bullet point includes a sub list, use object format. also use object format when using HTML strings.
      // @property {String} main the primary bullet point for the subsequent sub list / the bullet point that includes HTML
      // @property {[String]} sub the bullet points for this sub list
      tabContent: [
        // Diabetic
        {
          name: 'Diabetic',
          title: 'Diabetic Wound',
          section: [
            {
              title: 'Check',
              info: [
                'Patient blood sugar (every visit).',
                'Examine for lower extremity foot deformity (new visit).',
                ' Palpate for pulses (every visit).',
                'Is the patient wearing any type of special shoes, or does the patient have a history of poor fitting shoes (every visit).',
                'Vision (new visit).',
                'Sensory test with monofilament (new visit)',
              ],
            },
            {
              title: 'Recommendations For Open Ulcer',
              info: [
                'Consider total removable contact cast with diamond cut outs for offloading.',
                'Tight glycemic control.',
                'Smoking cessation counseling.',
                {
                  main: 'Perform probe to bone test to see if bony involvement:',
                  sub: ['If new wound consider X-ray followed by MRI to rule out osteomyelitis.'],
                },
              ],
            },
            {
              title: 'Presence of Devitalized Tissue = (slough or necrosis)',
              info: [
                'If infected consider antibiotics.',
                'Full debridement of tissue including callous edge.',
                'If healing less than 30% reduction in size in first month consider changing dressing.',
              ],
            },
            {
              title: 'Dressing',
              info: [
                {
                  main: 'If Presence of Devitalized, Recommend (in order of best practice):',
                  sub: ['Collagenase', 'Hydrogel', 'Calcium Alginate'],
                },
                {
                  main: 'If 100% Granulating with Minimal Drainage:',
                  sub: ['Collagen', 'Hydrogel'],
                },
                {
                  main: 'If Significant Drainage, Recommend:',
                  sub: ['Calcium Alginate', 'Foam', 'Gauze Dressing'],
                },
              ],
            },
          ],
        },

        // Arterial
        {
          name: 'Arterial',
          title: 'Arterial Ulcer',
          section: [
            {
              title: 'Assessment',
              info: [
                'All patients with lower extremity wounds should have an arterial examination as a component of their physical exam.',
                'Absence of pulses on examination should lead to further diagnostic discovery with arterial Doppler Examination.',
                'Level of arterial occlusion can be measured with Arterial Ultrasound with waveforms, and ankle brachial indices.',
              ],
            },
            {
              title: 'Wound Dressing',
              info: [
                'In arterial ulcers with sufficient arterial inflow to support healing, use a dressing that will maintain a moist wound-healing environment.',
                'Dry gangrene or eschar is best left dry until revascularization is successful.',
                'Avoid applying saline to dry wounds as it will soften them.',
              ],
            },
            {
              title: 'Debridement',
              info: [
                'Dry lower extremity wounds with eschar should not be debrided.',
                'Wounds can be probed by the finger to test for purulence or fluctuance in the eschar and if present should be debrided.',
                'Open wounds should be debrided of slough or necrosis to prevent amputation and promote healing.',
              ],
            },
            {
              title: 'Smoking Cessation',
              info: [
                'Mortality, risk of amputation, peripheral graft occlusion and cardiovascular event rate are all dose-dependently correlated with tobacco smoking.',
                'Definitely document smoking cessation with PAD patients and if they continue to smoke (every visit).',
              ],
            },
            {
              title: 'Hyperlipidemia',
              info: [
                'Lowering LDL levels reduces risks of mortality and cardiovascular events.',
                'Statins are the recommended primary agents.',
              ],
            },
            {
              title: 'Antihypertensive Agents',
              info: [
                'Reduce risk of cardiovascular events.',
                'ACE inhibitors may have additional benefit beyond blood pressure lowering.',
              ],
            },
          ],
        },

        // Venous
        {
          name: 'Venous',
          title: 'Venous Ulcer',
          section: [
            {
              title: 'Compression Therapy Gold Standard',
              info: [
                'In a patient with a venous leg ulcer, we recommend compression therapy over no compression therapy to increase venous leg ulcer healing rate.',
                'In a patient with a healed venous leg ulcer, we suggest compression therapy to decrease the risk of ulcer recurrence.',
                'We suggest the use of multicomponent compression bandage over single-component bandages for the treatment of venous leg ulcers.',
                'In a patient with a venous leg ulcer and underlying arterial disease, we do not suggest compression bandages or stockings if the ankle-brachial index is 0.5 or less or if absolute ankle pressure is less than 60 mm Hg.',
              ],
            },
            {
              title: 'Dressings',
              info: [
                'We suggest applying a topical dressing that will manage venous leg ulcer exudate and maintain a moist, warm wound bed. We suggest selection of a primary wound dressing that will absorb wound exudate produced by the ulcer (alginates, foams) and protect the periulcer skin.',
                'Insufficient evidence suggesting that a specific primary dressing results in a higher rate of wound closure. ',
                'Most VLUs produce large amounts of exudate, with high concentrations of proteases and inflammatory cytokines that may damage surrounding healthy skin.',
                'We recommend use of dressings that will manage wound exudate and maintain a moist wound bed.',
                'Primary dressings with high absorptive capabilities, including foams, alginates, and other specialty dressings, are often selected for the primary coverage layer for heavily exudative VLUs.',
                'We recommend against the routine use of topical antimicrobial-containing dressings in the treatment of noninfected venous leg ulcers.',
              ],
            },
            {
              title: 'Debridement',
              info: [
                'We recommend that venous leg ulcers receive thorough debridement at their initial evaluation to remove obvious necrotic tissue, excessive bacterial burden, and cellular burden of dead and senescent cells.',
                'We suggest that additional maintenance debridement be performed to maintain the appearance and readiness of the wound bed for healing.',
                'We recommend that local anesthesia (topical or local injection) be administered to minimize discomfort associated with surgical venous leg ulcer debridement.',
                'We recommend that surgical debridement be performed for venous leg ulcers with slough, nonviable tissue, or eschar. Serial wound assessment is important in determining the need for repeated debridement.',
                'We do not suggest enzymatic debridement over surgical debridement.',
              ],
            },
            {
              title: 'Infection',
              info: [
                'We suggest against routine culture of venous leg ulcers and only to obtain wound culture specimens when clinical evidence of infection is present.',
              ],
            },
            {
              title: 'Wound Biopsy',
              info: [
                'We recommend wound biopsy for leg ulcers that do not improve with standard wound and compression therapy after 4 to 6 weeks of treatment and for all ulcers with atypical features.',
              ],
            },
            {
              title: 'Exam',
              info: [
                'We recommend arterial pulse examination and measurement of ankle-brachial index on all patients with venous leg ulcer.',
                'Approximately 15% to 25% of patients with VLUs will have a concomitant PAD component.',
                'We recommend comprehensive venous duplex ultrasound examination of the lower extremity in all patients with suspected venous leg ulcer.',
              ],
            },
            {
              title: 'Cleansing',
              info: [
                'We suggest that venous leg ulcers be cleansed initially and at each dressing change with a neutral, nonirritating, nontoxic solution, performed with a minimum of chemical or mechanical trauma.',
              ],
            },
            {
              title: 'Negative Pressure Wound Therapy',
              info: [
                'We suggest against routine primary use of negative pressure wound therapy for venous leg ulcers.',
              ],
            },
            {
              title: 'Electrical Stimulation',
              info: [
                'We suggest against electrical stimulation therapy for venous leg ulcers.',
              ],
            },
            {
              title: 'Ultrasound Therapy',
              info: [
                'We suggest against routine ultrasound therapy for venous leg ulceration. ',
              ],
            },
          ],
        },

        // Pressure
        {
          name: 'Pressure',
          title: 'Pressure Injuries',
          section: [
            {
              title: 'Complete Examination of Body Areas for New Injury (every visit)',
              info: [],
            },
            {
              title: 'Debridement',
              info: [
                'Debride devitalized tissue within the wound bed or edge of pressure ulcers when appropriate to the individual\'s condition and consistent with overall goals of care.',
                'Debride the wound bed when the presence of biofilm is suspected or confirmed.',
                'When a wound has delayed healing (i.e., four weeks or more) and fails to respond to standard wound care and/or antimicrobial therapy, have a high index of suspicion of the presence of biofilm.',
                'Surgical/sharp debridement is recommended in the presence of extensive necrosis, advancing cellulitis, crepitus, fluctuance, and/or sepsis secondary to ulcer-related infection.',
                'Use sterile instruments for conservative sharp and surgical/sharp debridement.',
                {
                  main: '<strong>Do not debride stable, hard, dry eschar in ischemic limbs.</strong>',
                },
                'Consider the use of topical antiseptics in conjunction with maintenance debridement to control and eradicate suspected biofilm in wounds with delayed healing.',
              ],
            },
            {
              title: 'Infection Considerations',
              info: [
                {
                  main: 'Have a high index of suspicion of local infection in a pressure ulcer in the presence of:',
                  sub: [
                    'Lack of signs of healing for two weeks',
                    'Friable granulation tissue',
                    'Malodor',
                    'Increased pain in the ulcer',
                    'Increased heat in the tissue around the ulcer',
                    'Increased drainage from the wound',
                    'An ominous change in the nature of the wound drainage (e.g., new onset of bloody drainage, purulent drainage)',
                    'increased necrotic tissue in the wound bed',
                    'And/or pocketing or bridging in the wound bed',
                  ],
                },
              ],
            },
            {
              title: 'General Recommendation',
              info: [
                {
                  main: '<strong>Avoid positioning the individual on an area of erythema whenever possible.</strong>',
                },
                'Keep the skin clean and dry.',
                'Do not massage or vigorously rub skin that is at risk of pressure ulcers.',
                'Develop and implement an individualized continence management plan.',
                'Protect the skin from exposure to excessive moisture with a barrier product in order to reduce the risk of pressure damage.',
                'Consider using a skin moisturizer to hydrate dry skin in order to reduce risk of skin damage.',
              ],
            },
            {
              title: 'Consider applying a polyurethane foam dressing to bony prominences (e.g., heels, sacrum) for the prevention of pressure ulcers in anatomical areas frequently subjected to friction and shear.',
              info: [],
            },
            {
              title: 'Nutrition',
              info: [
                {
                  main: '<strong>Assess the weight status of each individual to determine weight history and identify significant weight loss (≥ 5% in 30 days or ≥ 10% in 180 days).</strong>',
                },
                'Assess the individual\'s ability to eat independently.',
                'Assess the adequacy of total nutrient intake (i.e., food, fluid, oral supplements and enteral/parenteral feeds).',
              ],
            },
            {
              title: 'Repositioning',
              info: [
                'Use the 30° tilted side-lying position (alternately, right side, back, left side) or the prone position if the individual can tolerate this and her/his medical condition allows.',
                'Encourage individuals who can reposition themselves to sleep in a 30° to 40° side-lying position or flat in bed if not contraindicated.',
                'Avoid lying postures that increase pressure, such as the 90° side-lying position, or the semirecumbent position.',
                'Limit head-of-bed elevation to 30° for an individual on bedrest unless contraindicated by medical condition or feeding and digestive considerations.',
                {
                  main: '<strong>Do not position an individual directly on a pressure ulcer.</strong>',
                },
                'Position the individual off area(s) of suspected deep tissue injury with intact skin. If pressure over the area cannot be relieved by repositioning, select an appropriate support surface.',
                'Pressure reduces perfusion to injured tissues. Continued pressure on an existing pressure ulcer will delay healing and may cause additional deterioration.',
                'Continue to turn and reposition the individual regardless of the support surface in use. Establish turning frequency based on the characteristics of the support surface and the individual\'s response.',
                'No support surface provides complete pressure relief.',
                {
                  main: 'If sitting in <strong>a chair</strong> is necessary for individuals with pressure ulcers on the sacrum/coccyx or ischia, limit sitting to three times a day in periods <strong>of 60 minutes or less</strong>. Consult a seating specialist to prescribe an appropriate seating surface and/or positioning techniques to avoid or minimize pressure on the ulcer.',
                },
              ],
            },
            {
              title: 'Assessment',
              info: [
                {
                  main: 'Verify that there <strong>is clinical agreement</strong> in pressure ulcer classification amongst the health professionals responsible for classifying pressure ulcers.',
                },
                {
                  main: '<strong>Reassess the individual, the pressure ulcer and the plan of care if the ulcer does not show signs of healing as expected despite appropriate local wound care, pressure redistribution, and nutrition.</strong>',
                },
              ],
            },
            {
              title: 'Healing Expectations',
              info: [
                'Expect some signs of pressure ulcer healing within two weeks.',
                'Adjust expectations for healing in the presence of multiple factors that impair wound healing.',
              ],
            },
            {
              title: 'Pain',
              info: [
                'Assess all individuals for pain related to a pressure ulcer or its treatment and document findings.',
                {
                  main: 'An initial pain assessment should include the following four elements:',
                  sub: [
                    'A detailed pain history including the character, intensity and duration of pressure ulcer pain',
                    'A physical examination that includes a neurological component',
                    'A psychosocial assessment',
                    'And an appropriate diagnostic work-up to determine the type and cause of the pain',
                  ],
                },
                {
                  main: "<strong>Encourage individuals to request a 'time out' during any procedure that causes pain.</strong>",
                },
                'Reduce pressure ulcer pain by keeping the wound bed covered and moist, and using a non-adherent dressing.',
              ],
            },
            {
              title: 'Cleansing',
              info: [
                'Cleanse the pressure ulcer at the time of each dressing change.',
                'Apply cleansing solution with sufficient pressure to cleanse the wound without damaging tissue or driving bacteria into the wound.',
                'Consider using cleansing solutions with surfactants and/or antimicrobials to clean pressure ulcers with debris, confirmed infection, suspected infection, or suspected high levels of bacterial colonization.',
                {
                  main: '<strong>Hydrogen peroxide is highly toxic to</strong> tissues even at low concentrations and should not be used as a preferred topical antiseptic. Its use should be totally avoided in cavity wounds due to the risk of surgical emphysema and gas embolus.',
                },
                'Caution: Iodine products should be avoided in patients with impaired renal failure, history of thyroid disorders or known iodine sensitivity.',
                {
                  main: '<strong>Sodium hypochlorite (Dakin\'s solution) is cytotoxic at all concentrations and should be used with caution</strong>, at concentrations no greater than 0.025%, for short periods only when no other appropriate option is available. There is a risk of acidosis when acetic acid is used for extended periods over large wound surface areas.',
                },
              ],
            },
            {
              title: 'Dressing Recommendations',
              info: [
                {
                  main: 'Consider:',
                  sub: [
                    'Ability to keep the wound bed moist',
                    'Need to address bacterial bioburden',
                    'Nature and volume of wound exudate',
                    'Condition of the tissue in the ulcer bed',
                    'Condition of periulcer skin',
                  ],
                },
              ],
            },
          ],
        },
      ],
    }
  },
  watch: {
  },
  mounted() {
  },
  methods: {
    checkIfObj(info) {
      return typeof info === 'object'
    },
  },
}
</script>

<style lang="scss" scoped>
.bpg-modal-card {
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}

.bpg-header {
  flex-shrink: 0;
}

.bpg-main-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
}

.bpg-tabs {
  flex-shrink: 0;
  border-bottom: 1px solid #ccc;

  ::v-deep .v-tab {
    border-right: 1px solid #ccc;

    &:last-child {
      border-right: none;
    }
  }
}

.bpg-content {
  flex-grow: 1;
  overflow-y: auto;
}
</style>